import { deepSearchForFocusableSelectors } from './AccessibilityUtils';
import { isTouchDevice } from './DeviceUtils';
import { MediaQueryChangeEvent, MediaQueryService } from '@workbench/core';
import { mobileBreakpoints } from '../MediaQueryService/MediaQueryUtils';

const myMediaQueryService = new MediaQueryService(window);
/*
 * This manager will help one single component to keep track of the
 * focus especially when dealing with web components.
 * This class was made to be used on the Header which has a role="menu"
 * behavior for the flyout, so some methods might not apply to your use case and
 * might need to be more generalized.
 *
 * Always initialize this class by providing the parent component and never the component
 * itself
 */
export class AccessibilityFocusManager {
  private _focusableElementsList: HTMLElement[] = [];
  private _focusableTags = ['A', 'BUTTON'];
  private _focusableSelector = '';
  private _currentListIndex = -1;
  private _shouldManageFocus = true;
  private _isMobile = false;
  public isInitialized = false;

  /**
   * Validates if the index can move forward or backwards. If we are in the last element and try
   * to move forwards or in the last and try to move backwards this methods will make sure that
   * the focus will behave in a rolling pattern.
   *
   * If last element + move forwards = focus on first element
   * If first element + move backwards = focus on last element
   * @param isForwards - indicates if the focus is to move forwards or backwards
   */
  private _generateNextValidIndex(isForwards: boolean): number {
    const lastFocusableIndex = this._focusableElementsList.length - 1;

    if (isForwards) {
      const nextIndex = this._currentListIndex + 1;
      return nextIndex > lastFocusableIndex ? 0 : nextIndex;
    } else {
      const previousIndex = this._currentListIndex - 1;
      return previousIndex < 0 ? lastFocusableIndex : previousIndex;
    }
  }

  /**
   * Get every focusable children of a given parent element.
   *
   * @param parentElement - the parent element of the focusable elements
   */
  private _buildElementsList(parentElement: Element | null): void {
    if (parentElement) {
      const childList: Element[] = Array.from(parentElement.querySelectorAll(this._focusableSelector));

      this._buildChildList(childList);
    }
  }

  /**
   * Found elements need to mapped to their corresponding focusable elements, be it anchors or buttons
   * We could focus directly on the parent with the tabindex but that is not the expected behavior of
   * voice over application.
   *
   * For every element that is not focused we assign -1:
   * https://www.w3.org/TR/wai-aria-practices-1.1/#kbd_roving_tabindex
   *
   * Unfortunately when dealing with web components we need an additional query so that we can find elements
   * inside the shadow root. This is done inside the deepSearchForFocusableSelectors function
   * @param childList - List of children to be iterated over
   */
  private _buildChildList(childList: Element[]): void {
    childList.forEach((childElement, outerIndex) => {
      const focusableElements = deepSearchForFocusableSelectors(childElement, this._focusableTags);

      focusableElements.elements.forEach((element, index) => {
        this._focusableElementsList.push(element as HTMLElement);

        if (outerIndex !== 0 || index !== 0) {
          element.setAttribute('tabindex', '-1');
        }
      });

      if (focusableElements.hasExternal) {
        this._shouldManageFocus = false;
      }
    });
  }

  private _setAndListenForMediaQueryChanges() {
    const currentMq = myMediaQueryService.getCurrentMediaQuery();
    this._isMobile = mobileBreakpoints.includes(currentMq);

    window.addEventListener('wbresize', ((e: CustomEvent<MediaQueryChangeEvent>) => {
      this._isMobile = mobileBreakpoints.includes(e.detail.current);
    }) as EventListener);
  }

  /**
   * Set the next focusable element
   *
   * @param nextIndex - Indicates the next of the next element. This is calculated on the
   *  methods that call this function using a "roll over" approach. if index = last, move to first
   * and vice-versa
   */
  private _setCurrentFocus(nextIndex: number): HTMLElement {
    if (this._currentListIndex !== -1) {
      this._focusableElementsList[this._currentListIndex]?.setAttribute('tabindex', '-1');
    }
    this._currentListIndex = nextIndex;

    const currentElement = this._focusableElementsList[this._currentListIndex];
    currentElement?.removeAttribute('tabindex');

    if (this._focusableTags.includes(currentElement?.tagName) && this._isFocusable()) {
      currentElement.focus();
    }

    return this._focusableElementsList[this._currentListIndex];
  }

  private _isFocusable(): boolean {
    return !isTouchDevice() || !this._isMobile;
  }

  /**
   * Focus on next element on the list.
   * If we are already on the last element,
   * returns 0 (moves to the first).
   */
  public focusOnNextElement(): void {
    const nextIndex = this._generateNextValidIndex(true);

    this._setCurrentFocus(nextIndex);
  }

  /**
   * Focus on previous element on the list.
   * If we are already on the first element,
   * returns list.length - 1 (moves to the last).
   */
  public focusOnPreviousElement(): HTMLElement {
    const nextIndex = this._generateNextValidIndex(false);
    return this._setCurrentFocus(nextIndex);
  }

  /**
   * Tries to click on the current focus element. Returns that element if
   * successful, or null otherwise.
   *
   * The reason for this, is that on application like menus, anchors and buttons
   * have different behaviors, so this method accepts a value which will indicate
   * if anchor are clickable or not.
   *
   * An example of such a case, is if the user is currently focused on an anchor on an
   * open flyout. if he presses the right arrow, the focus should move to the next flyout
   * on the navigation list. But if he would otherwise be focused on a button, the right arrow should open
   * that button's associated flyout.
   *
   * This class has nothing to do with actual enter or space clicks, this is purely for actions that simulate
   * a click (like clicking the right arrow when focused on a topic)
   *
   * @param allowButtonsOnly - Indicates if anchor clicks are allowed
   */
  public tryToOpenFocusedElement(allowButtonsOnly = false): HTMLElement | null {
    let clickedElement = null;
    let isAnchorOnButtonOnlyCall = false;
    this._currentListIndex = this._currentListIndex !== -1 ? this._currentListIndex : 0;

    if (allowButtonsOnly) {
      isAnchorOnButtonOnlyCall = this._focusableElementsList[this._currentListIndex].tagName === 'A';
    }

    if (!isAnchorOnButtonOnlyCall) {
      this._focusableElementsList[this._currentListIndex].click();
      clickedElement = this._focusableElementsList[this._currentListIndex];
    }

    return clickedElement;
  }

  public reset(): void {
    const index = this._currentListIndex;
    if (index === -1) {
      return;
    }

    this._focusableElementsList[this._currentListIndex].setAttribute('tabindex', '-1');
    this._currentListIndex = 0;
    this._focusableElementsList[this._currentListIndex].removeAttribute('tabindex');
  }

  public get shouldManageFocus(): boolean {
    return this._shouldManageFocus;
  }

  /**
   * Initializes this instance with a given parent element (or class of said parent element)
   *
   * @param parentElement - Parent element on which the focus list will be build upon
   * @param parentClass - class to search for parent on which the focus list will be built upon
   * @param focusableSelector - the main selector of every focusable children inside the parent
   */
  public init(parent: HTMLElement | string, focusableSelector: string) {
    this._focusableSelector = focusableSelector;

    if (typeof parent === 'string') {
      const parentElement = document.querySelector(`.${parent}`);
      this._buildElementsList(parentElement);
    } else if (parent) {
      this._buildElementsList(parent);
    }

    this._setAndListenForMediaQueryChanges();
    this.isInitialized = true;
  }

  public getFocusableList(): HTMLElement[] {
    return this._focusableElementsList;
  }

  public focusOnFirstElement() {
    this._setCurrentFocus(0);
  }

  public focusOnLastElement() {
    const listLength = this._focusableElementsList.length - 1;
    this._setCurrentFocus(listLength);
  }
}
